<script lang="ts" setup>
import type { CategoryTree } from "~~/models/categories";
import type { CatalogPageRecord, ProductGroupsQuery_allProductGroups_ProductGroupRecord } from "~~/server/graphql/datocms/generated";

const { cmsData, productGroups } = defineProps<{
  cmsData: CatalogPageRecord;
  categoryTree: CategoryTree;
  productGroups?: Array<ProductGroupsQuery_allProductGroups_ProductGroupRecord> | null | undefined;
}>();

const isOpen = ref(false);
const contentRounded = ref("rounded-lg");

const triggerIcon = computed(() => isOpen.value ? "i-ph-x" : "i-ph-caret-down");

const categoryMenu = ref(null);
onClickOutside(categoryMenu, _ => isOpen.value = false);

function getCategoryIcon(categoryId: number) {
  return productGroups
    ?.filter((pg: ProductGroupsQuery_allProductGroups_ProductGroupRecord) => pg.optCategory)
    ?.find((pg: ProductGroupsQuery_allProductGroups_ProductGroupRecord) => (pg.optCategory! as Array<{ name: string; value: string }>)
      .map((d: { name: string; value: string }) => d.value)
      .includes(categoryId.toString()))
    ?.icon;
}
</script>

<template>
  <div ref="categoryMenu" class="relative z-40">
    <UButton variant="solid" color="blue" size="md" :icon="triggerIcon" trailing @click="isOpen = !isOpen">
      {{ cmsData.actionShowAllCategories }}
    </UButton>

    <div v-if="isOpen" class="py-2xs absolute top-[55px] bg-[#F3F1EC]" :class="contentRounded">
      <ul>
        <li v-for="category of categoryTree.getFirstLevel()" :key="category.id" class="category-sub-menu px-2xs pb-2xs last:pb-0">
          <NuxtLinkLocale :to="`/catalog${categoryTree.getPath(category.id)}`">
            <div
              class="p-xs flex w-[275px] cursor-pointer items-center justify-between rounded-lg bg-white hover:bg-[#F8F7F4]"
              @mouseover="contentRounded = 'rounded-s-lg'" @mouseleave="contentRounded = 'rounded-lg'"
            >
              <div class="gap-sm flex shrink-0 items-center">
                <img v-if="getCategoryIcon(category.id)" :src="getCategoryIcon(category.id)?.url" alt="" class="size-[32px] shrink-0">
                <span class="text-sm font-bold">{{ categoryTree.getName(category.id) }}</span>
              </div>
              <UIcon name="i-ph-caret-right" />
            </div>
          </NuxtLinkLocale>

          <div
            class="category-sub-menu__links p-2xs absolute left-[283px] top-0 min-h-full rounded-e-lg bg-[#F3F1EC] lg:w-[837px] xl:w-[1125px]"
          >
            <div class="p-3xl bg-white">
              <h4 class="text-blue mb-lg">
                <NuxtLinkLocale class="hover:underline" :to="`/catalog${categoryTree.getPath(category.id)}`">
                  {{ categoryTree.getName(category.id) }}
                </NuxtLinkLocale>
              </h4>

              <ul class="gap-3xl grid grid-cols-2 xl:grid-cols-3">
                <li v-for="child of categoryTree.getChildren(category.id)" :key="child.id" class="gap-2xs flex flex-col">
                  <NuxtLinkLocale
                    class="font-semibold hover:underline"
                    :to="`/catalog${categoryTree.getPath(child.id)}`"
                  >
                    {{ categoryTree.getName(child.id) }}
                  </NuxtLinkLocale>

                  <ul class="gap-2xs flex flex-col">
                    <li v-for="subChild of categoryTree.getChildren(child.id)" :key="subChild.id">
                      <NuxtLinkLocale
                        class="hover:underline"
                        :to="`/catalog${categoryTree.getPath(subChild.id)}`"
                      >
                        {{ categoryTree.getName(subChild.id) }}
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </li>

        <li class="px-2xs pb-2xs last:pb-0">
          <NuxtLinkLocale to="/happy-deals">
            <div
              class="p-xs flex w-[275px] cursor-pointer items-center justify-between rounded-lg bg-white hover:bg-[#F8F7F4]"
            >
              <div class="gap-sm flex shrink-0 items-center">
                <img src="/icons/promo.svg" alt="" class="size-[32px] shrink-0">
                <span class="text-primary text-sm font-bold">{{ cmsData.happyDealsCategoryTitle }}</span>
              </div>
              <UIcon name="i-ph-caret-right" class="text-primary" />
            </div>
          </NuxtLinkLocale>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.category-sub-menu {
  &:hover {
    .category-sub-menu__links {
      display: block;
    }
  }

  .category-sub-menu__links {
    display: none;
  }
}
</style>
